import { useState } from 'react';

import { ApiError, api, type CreatedInvite } from '../lib/api';
import { Alert, Badge, Card, Field, SecretReveal, formatTime, useLoader } from '../components/ui';

const ROLE_KIND: Record<string, string> = {
  owner: 'info',
  admin: 'info',
  member: 'muted',
};

/**
 * Section 4.1 and 4.11: team members and invitations.
 *
 * Rendered inside Settings rather than as its own nav item — a two-person org
 * does not need a top-level destination for one table.
 */
export function Team({ currentUserId }: { currentUserId: string }) {
  const { data, error, loading, reload } = useLoader(() => api.team.get());
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // `seat_limit_reached` is a plan problem, not a problem with the address, so
  // it stays at form level; anything else the server rejects is about the email.
  const [emailError, setEmailError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedInvite | null>(null);

  const canManage = data?.your_role === 'owner' || data?.your_role === 'admin';

  async function invite(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    setEmailError(null);
    try {
      setCreated(await api.team.invite(email, role));
      setEmail('');
      reload();
    } catch (err) {
      // A seat limit is a plan problem: the address is fine and editing it will
      // not help, so that one belongs above the form. Everything else the server
      // rejects here is about the address itself.
      if (err instanceof ApiError && err.code !== 'seat_limit_reached') {
        setEmailError(err.message);
      } else {
        setFormError(err instanceof Error ? err.message : 'Could not create invite');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card title="Team">
        {error && <Alert kind="error">{error}</Alert>}

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.members ?? []).map((member) => (
                <tr key={member.user_id}>
                  <td className="mono">
                    {member.email}
                    {member.user_id === currentUserId && (
                      <>
                        {' '}
                        <Badge kind="ok">you</Badge>
                      </>
                    )}
                  </td>
                  <td>
                    <Badge kind={ROLE_KIND[member.role] ?? 'muted'}>{member.role}</Badge>
                  </td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {member.joined_at ? formatTime(member.joined_at) : 'pending'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {canManage &&
                      member.user_id !== currentUserId &&
                      member.role !== 'owner' && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={async () => {
                            if (
                              !window.confirm(
                                `Remove ${member.email}? Their sessions end immediately.`,
                              )
                            ) {
                              return;
                            }
                            await api.team.removeMember(member.user_id);
                            reload();
                          }}
                        >
                          Remove
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && !data && <div className="empty">Loading…</div>}
      </Card>

      {created && (
        <Card title={`Invitation for ${created.email}`}>
          <SecretReveal label="Invite link" value={created.invite_url} />
          <Alert kind="info">
            <strong>Send this link to them yourself.</strong> The platform does not
            email invitations yet — SES production access is still unconfirmed, and an
            invite that depends on delivery we cannot guarantee would fail silently.
            The link works once and expires in 7 days.
          </Alert>
          <button type="button" className="btn btn-sm" onClick={() => setCreated(null)}>
            Done
          </button>
        </Card>
      )}

      {canManage && (
        <Card title="Invite someone">
          {formError && <Alert kind="error">{formError}</Alert>}
          <form onSubmit={invite} className="row" style={{ alignItems: 'flex-end' }}>
            <Field
              id="invite-email"
              label="Email"
              error={emailError}
              style={{ marginBottom: 0, flex: '1 1 240px' }}
            >
              {(props) => (
                <input
                  {...props}
                  type="email"
                  required
                  placeholder="colleague@yourcompany.com"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (emailError) setEmailError(null);
                  }}
                />
              )}
            </Field>
            <Field
              id="invite-role"
              label="Role"
              style={{ marginBottom: 0, width: 150 }}
            >
              {(props) => (
                <select
                  {...props}
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              )}
            </Field>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create invite'}
            </button>
          </form>
          <p className="hint" style={{ marginTop: 10 }}>
            Owner cannot be invited — transfer ownership as a separate, deliberate
            action rather than by picking a word in a dropdown.
          </p>
        </Card>
      )}

      {(data?.pending_invites ?? []).length > 0 && (
        <Card tight title={`Pending invites (${data?.pending_invites.length})`}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invited by</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data?.pending_invites ?? []).map((invite) => (
                  <tr key={invite.id}>
                    <td className="mono">{invite.email}</td>
                    <td>
                      <Badge kind="muted">{invite.role}</Badge>
                    </td>
                    <td className="mono">{invite.invited_by ?? '—'}</td>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                      {formatTime(invite.expires_at)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={async () => {
                            await api.team.revokeInvite(invite.id);
                            reload();
                          }}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!canManage && data && (
        <Alert kind="info">
          You are a <strong>{data.your_role}</strong>, so you can see the team but not
          change it. Inviting and removing people is limited to owners and admins.
        </Alert>
      )}
    </>
  );
}
